import { PostV1CompaniesCompanyIdOrdersOrderIdJobsJobCardPdfsData } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';

import { Filters as AssignedFilters } from '~store/OrdersAssignedStore';
import { Filters as DispatchFilters } from '~store/OrdersDispatchStore';
import prefixKeys from '~utils/prefixKeys';

type OrderJobsRenderBody = NonNullable<
  PostV1CompaniesCompanyIdOrdersOrderIdJobsJobCardPdfsData['body']
>;
type OrderJobsFilterBody = Required<Pick<OrderJobsRenderBody, 'filter'>> &
  Pick<OrderJobsRenderBody, 'search'>;

interface BuildOrderJobsFilterOptions {
  assignedFilters: AssignedFilters;
  dispatchFilters: DispatchFilters;
  pathname: string;
}

export function toOrderJobsQueryParams({ filter, search }: OrderJobsFilterBody) {
  const { equipment, ...topLevelFilter } = filter;

  return {
    ...prefixKeys('filter', topLevelFilter),
    ...(equipment ? prefixKeys('filter[equipment]', equipment) : {}),
    ...(search ? prefixKeys('search', search) : {}),
  };
}

// Shared by the order card's job list and its job card PDF render, so a PDF covers exactly
// the jobs the card lists. The Assigned page owns its own driver and date filters.
export default function buildOrderJobsFilter({
  assignedFilters,
  dispatchFilters,
  pathname,
}: BuildOrderJobsFilterOptions) {
  const isAssignedPage = pathname.includes('/assigned');
  const isDispatchPage = pathname.includes('/dispatch');
  const pageFilters = isAssignedPage ? assignedFilters : dispatchFilters;
  const driverIds = isAssignedPage || isDispatchPage ? pageFilters.driverIds : undefined;
  const equipmentOwnerCompanyIds = isDispatchPage
    ? dispatchFilters.equipmentOwnerCompanyIds
    : undefined;
  const searchTerm = dispatchFilters.search?.trim();

  const body: OrderJobsFilterBody = {
    filter: {
      ...(dispatchFilters.customerAccountIds?.length
        ? { customer_account_ids: dispatchFilters.customerAccountIds }
        : {}),
      ...(isDispatchPage && dispatchFilters.dispatchStatuses?.length
        ? { dispatch_statuses: dispatchFilters.dispatchStatuses }
        : {}),
      ...(driverIds?.length ? { driver_ids: driverIds } : {}),
      ...(dispatchFilters.dropoffSites?.length
        ? { dropoff_site_ids: dispatchFilters.dropoffSites }
        : {}),
      ...(pageFilters.endDate?.length
        ? { end_date: dayjs.tz(pageFilters.endDate).endOf('day').toISOString() }
        : {}),
      ...(equipmentOwnerCompanyIds?.length
        ? { equipment: { company_ids: equipmentOwnerCompanyIds } }
        : {}),
      ...(dispatchFilters.projectsExternalIds?.length
        ? { external_ids: dispatchFilters.projectsExternalIds }
        : {}),
      ...(dispatchFilters.foremen?.length
        ? { foreman_ids: dispatchFilters.foremen }
        : {}),
      ...(dispatchFilters.materialIds?.length
        ? { material_ids: dispatchFilters.materialIds }
        : {}),
      ...(dispatchFilters.pickUpSites?.length
        ? { pickup_site_ids: dispatchFilters.pickUpSites }
        : {}),
      ...(dispatchFilters.projectIds?.length
        ? { project_ids: dispatchFilters.projectIds }
        : {}),
      ...(dispatchFilters.siteTypes?.length
        ? { site_types: dispatchFilters.siteTypes }
        : {}),
      ...(pageFilters.startDate?.length
        ? { start_date: dayjs.tz(pageFilters.startDate).startOf('day').toISOString() }
        : {}),
      ...(dispatchFilters.jobStates?.length ? { states: dispatchFilters.jobStates } : {}),
      ...(dispatchFilters.vendorAccountIds?.length
        ? { vendor_account_ids: dispatchFilters.vendorAccountIds }
        : {}),
    },
    ...(searchTerm?.length ? { search: { datagrid: searchTerm } } : {}),
  };

  return body;
}
